/**
 * @file RepContractDetail.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description One contract opened on the reps' contracts screen
 *              (RepContracts — Cam, 28 Oct 2026, O4: "see orders from
 *              contracts, product groups, costing, pricing, margin info"):
 *              its details, its products (a group of mills opens into each
 *              supplier's product, a straights contract into its months, a
 *              milk powder price into its dated prices), the orders counted
 *              against it and the PO lines counted through a PO link or the
 *              contract number. Everything comes from GET
 *              /api/rep-contracts/contract?key= — the server decides whose
 *              it is and whether cost shows.
 *
 *   Every quantity is in the order line's OWN unit with its tonnes beside it
 *   (QtyWithTonnes), every price says what it is per ("£170.00 /500kg tote"),
 *   so a rep never has to guess whether a figure is per tonne or per tote.
 */

import React from 'react';
import QtyWithTonnes from './QtyWithTonnes';
import { useEntityModal } from './EntityModalProvider';
import { formatDateDDMMYY } from '../utils/dateFormatters';
import { formatTonnes } from '../utils/numberFormatters';
import { formatPrice } from '../utils/currencyFormatter';
import { priceText, datesText, STATE_LABELS } from '../utils/repContractsView';

// pricing.contract_scope_kind's answers, in words.
const SCOPE_WORDS = {
  none: 'This customer',
  parent_only: 'This customer only (not its linked accounts)',
  group: 'This customer and its pricing group',
  children_only: 'Its linked accounts only',
  specific: 'Named accounts'
};

function marginCell(margin) {
  if (!margin) return null;
  return (
    <span className={`rcd-margin ${margin.amount < 0 ? 'neg' : 'pos'}`}>
      {formatPrice(margin.amount)} <span className="rcd-muted">({margin.percent.toFixed(1)}%)</span>
    </span>
  );
}

function Fact({ label, children }) {
  if (children === null || children === undefined || children === '' || children === false) return null;
  return (
    <div className="rcd-fact">
      <span className="rcd-fact-label">{label}</span>
      <span className="rcd-fact-value">{children}</span>
    </div>
  );
}

/** The products / months / dated prices of a contract that has more than one. */
function Lines({ contract, showCosts }) {
  const lines = contract.lines || [];
  const kind = contract.kind;
  if (lines.length <= 1 && kind !== 'milk_powder') return null;
  const unit = contract.price_unit;
  const heading = kind === 'straights' ? 'Months' : kind === 'milk_powder' ? 'Prices by date' : 'Products';
  return (
    <section className="rcd-section">
      <h5 className="rcd-heading">{heading}</h5>
      <div className={`rcd-lines${showCosts ? '' : ' no-costs'}`} role="list">
        {lines.map(l => {
          const name = kind === 'straights' || kind === 'milk_powder'
            ? l.label
            : [l.item && l.item.code, l.item && l.item.name].filter(Boolean).join(' ');
          const isNow = kind === 'milk_powder' ? l.in_force === 'current' : l.id === contract.current_line_id;
          return (
            <div className={`rcd-line${l.state !== 'live' ? ' is-dim' : ''}`} role="listitem" key={`${l.kind}:${l.id}`}>
              <div className="rcd-line-name">
                <span className="rcd-strong">{name || 'No product'}</span>
                {isNow && <span className="rc-pill rc-pill-primary">Now</span>}
                {l.state !== 'live' && <span className="rc-pill">{STATE_LABELS[l.state] || l.state}</span>}
                {l.item && l.item.archived && <span className="rc-pill rc-pill-warning">Product archived</span>}
                <div className="rcd-muted rcd-small">
                  {[
                    kind !== 'straights' && kind !== 'milk_powder' && l.supplier && l.supplier.name,
                    l.product_group && l.product_group.name && `Group: ${l.product_group.name}${l.product_group.additive ? ` (${l.product_group.additive})` : ''}`,
                    l.supplier_contract_number,
                    kind === 'straights' && l.supplier && l.supplier.name
                  ].filter(Boolean).join(' · ')}
                </div>
              </div>
              <div className="rcd-line-fig">
                <span className="rcd-fig-label">Price</span>
                <span className={!(l.price > 0) ? 'rcd-neg' : ''}>{l.price > 0 ? priceText(l.price, unit) : 'No price'}</span>
              </div>
              {showCosts && (
                <div className="rcd-line-fig">
                  <span className="rcd-fig-label">Cost</span>
                  <span>{l.cost > 0 ? priceText(l.cost, unit) : '—'}</span>
                </div>
              )}
              {showCosts && (
                <div className="rcd-line-fig">
                  <span className="rcd-fig-label">Margin</span>
                  {marginCell(l.margin) || <span className="rcd-muted">—</span>}
                </div>
              )}
              {kind !== 'milk_powder' && (
                <div className="rcd-line-fig rcd-line-usage">
                  <span className="rcd-fig-label">Ordered</span>
                  <span>
                    {kind === 'straights' && l.fulfilment ? l.fulfilment.text : formatTonnes((l.ordered && l.ordered.total) || 0)}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Orders({ contract, openOrder }) {
  const orders = contract.orders || [];
  const many = (contract.products || []).length > 1 || contract.kind === 'straights';
  return (
    <section className="rcd-section">
      <h5 className="rcd-heading">
        Orders
        {contract.usage && <span className="rcd-heading-note">{contract.usage.text}</span>}
      </h5>
      {((contract.usage && contract.usage.notes) || []).map(n => (
        <p key={n.key} className={`rc-unit-note tone-${n.tone}`}>
          <span className="material-symbols-rounded" aria-hidden="true">{n.tone === 'warning' ? 'warning' : 'scale'}</span>
          {n.text}
        </p>
      ))}
      {orders.length === 0 ? (
        <p className="rcd-empty">
          {contract.kind === 'milk_powder' && contract.usage && /not recorded/.test(contract.usage.text)
            ? contract.usage.text
            : 'No orders counted against this contract yet.'}
        </p>
      ) : (
        <div className="rcd-orders" role="list">
          <div className="rcd-order rcd-order-head" aria-hidden="true">
            <span>Order</span><span>Delivery</span><span>Quantity</span><span>Supplied</span><span>Sale price</span><span>Status</span>
          </div>
          {orders.map(o => (
            <div className={`rcd-order${o.archived ? ' is-dim' : ''}`} role="listitem" key={o.order_line_id}>
              <span className="rcd-order-ref">
                <button
                  type="button"
                  data-read-safe
                  className="po-badge-inline rcd-order-btn"
                  onClick={() => openOrder(o.order_id)}
                  title={`Open ${o.order_number}`}
                >
                  {o.order_number}
                </button>
                {o.split_from && <span className="rcd-muted rcd-small">split from {o.split_from}</span>}
                {o.source === 'manual' && <span className="rc-pill" title="Added to this contract by hand">By hand</span>}
                {many && o.product && <span className="rcd-muted rcd-small rcd-order-product">{o.product}</span>}
              </span>
              <span className="rcd-cell" data-label="Delivery">{formatDateDDMMYY(o.delivery_date)}</span>
              <span className="rcd-cell" data-label="Quantity">
                <QtyWithTonnes quantity={o.quantity} unitWeightKg={o.qty_facts && o.qty_facts.unit_weight_kg}
                  itemUnitWeightKg={o.qty_facts && o.qty_facts.item_unit_weight_kg}
                  accountCode={o.qty_facts && o.qty_facts.account_code} />
              </span>
              <span className="rcd-cell" data-label="Supplied">
                {o.supplied_quantity === null || o.supplied_quantity === undefined
                  ? <span className="rcd-muted">Not recorded</span>
                  : <QtyWithTonnes quantity={o.supplied_quantity} unitWeightKg={o.qty_facts && o.qty_facts.unit_weight_kg}
                      itemUnitWeightKg={o.qty_facts && o.qty_facts.item_unit_weight_kg}
                      accountCode={o.qty_facts && o.qty_facts.account_code} />}
              </span>
              <span className="rcd-cell" data-label="Sale price">
                {o.unit_price > 0
                  ? priceText(o.unit_price, o.price_per)
                  : <span className="rcd-muted">Not priced</span>}
              </span>
              <span className="rcd-cell rcd-status" data-label="Status">
                <span>{o.delivery_status || '—'}</span>
                <span className="rcd-muted rcd-small">{o.archived ? 'Archived' : (o.invoiced || '')}</span>
              </span>
            </div>
          ))}
        </div>
      )}
      {contract.hidden && contract.hidden.lines > 0 && (
        <p className="rcd-muted rcd-small rcd-hidden-note">
          {contract.hidden.lines} more line{contract.hidden.lines === 1 ? '' : 's'} on another rep’s customers’ orders
          {' '}{contract.hidden.lines === 1 ? 'counts' : 'count'} against this contract ({formatTonnes(contract.hidden.quantity)}) — not shown here.
        </p>
      )}
    </section>
  );
}

function PoLines({ contract }) {
  const po = contract.po_lines || [];
  if (!po.length) return null;
  return (
    <section className="rcd-section">
      <h5 className="rcd-heading">Purchase order lines counted against it</h5>
      <div className="rcd-po-lines" role="list">
        {po.map(p => (
          <div className="rcd-po" role="listitem" key={`${p.purchase_order_id}:${p.line_ord}`}>
            <span className="rcd-strong">{p.purchase_order_number}</span>
            <span className="rcd-muted">{formatDateDDMMYY(p.date)}</span>
            <span>{p.item_code || p.description || ''}</span>
            <span>
              <QtyWithTonnes quantity={p.quantity} unitWeightKg={p.qty_facts && p.qty_facts.unit_weight_kg}
                itemUnitWeightKg={p.qty_facts && p.qty_facts.item_unit_weight_kg}
                accountCode={p.qty_facts && p.qty_facts.account_code} />
            </span>
            <span className="rcd-muted rcd-small">{p.source_text}{p.status ? ` · ${p.status}` : ''}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function RepContractDetail({ contract, state, showCosts }) {
  const { openEntity } = useEntityModal();
  const openOrder = (id) => openEntity('customer_order', id);

  if (!state || state.loading) {
    return <div className="rcd rcd-loading" aria-busy="true">Loading its orders…</div>;
  }
  if (state.error) {
    return <div className="rcd rcd-error" role="alert">Couldn’t load this contract: {state.error}</div>;
  }
  const c = state.data || contract;
  const ours = (c.contract_numbers || []).filter(Boolean);
  const theirs = (c.supplier_contract_numbers || []).filter(Boolean);
  const scope = c.scope && c.scope.kind ? (SCOPE_WORDS[c.scope.kind] || c.scope.kind) : null;
  return (
    <div className="rcd">
      <div className="rcd-facts">
        <Fact label="Dates">{datesText(c.start_date, c.end_date)}</Fact>
        <Fact label="Period">{(c.periods || []).join(', ')}</Fact>
        <Fact label="Our contract no.">{ours.join(', ')}</Fact>
        <Fact label="Supplier’s contract no.">{theirs.join(', ')}</Fact>
        <Fact label="Delivery terms">{(c.delivery_terms || []).join(', ')}</Fact>
        <Fact label="Applies to">{scope}</Fact>
        <Fact label="Price is per">{c.price_unit ? (c.price_unit === 'T' ? 'tonne (T)' : c.price_unit) : 'not known — the product has no unit weight'}</Fact>
      </div>
      <Lines contract={c} showCosts={showCosts} />
      <Orders contract={c} openOrder={openOrder} />
      <PoLines contract={c} />
      {c.lines_hidden_archived > 0 && (
        <p className="rcd-muted rcd-small">
          {c.lines_hidden_archived} archived product{c.lines_hidden_archived === 1 ? '' : 's'} of this contract not listed — {c.lines_hidden_archived === 1 ? 'its' : 'their'} orders still count.
        </p>
      )}
    </div>
  );
}

export default RepContractDetail;
